import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => number;
      reset: (id?: number) => void;
      execute: (id?: number) => void;
    };
    __recaptchaOnLoad?: () => void;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.grecaptcha?.render) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    window.__recaptchaOnLoad = () => resolve();
    const s = document.createElement('script');
    s.src = 'https://www.google.com/recaptcha/api.js?onload=__recaptchaOnLoad&render=explicit';
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export interface CaptchaHandle {
  reset: () => void;
}

interface CaptchaProps {
  onVerify: (token: string) => void;
  resetTrigger?: number | string | boolean;
}

export const Captcha = forwardRef<CaptchaHandle, CaptchaProps>(({ onVerify, resetTrigger }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);
  const recaptchaSiteKey = (import.meta.env.VITE_RECAPTCHA_SITE_KEY || '').trim();
  const cb = useRef(onVerify);
  cb.current = onVerify;

  const reset = () => {
    if (widgetId.current !== null && window.grecaptcha?.reset) {
      try {
        window.grecaptcha.reset(widgetId.current);
      } catch (err) {
        /* Ignore reset errors */
      }
    }
    if (!recaptchaSiteKey) {
      cb.current('BYPASSED');
    } else {
      cb.current('');
    }
  };

  useImperativeHandle(ref, () => ({
    reset,
  }));

  useEffect(() => {
    if (resetTrigger !== undefined) {
      reset();
    }
  }, [resetTrigger]);

  useEffect(() => {
    if (!recaptchaSiteKey) {
      cb.current('BYPASSED');
      return;
    }

    let cancelled = false;
    loadScript().then(() => {
      if (cancelled || !containerRef.current || widgetId.current !== null || !window.grecaptcha) return;
      try {
        widgetId.current = window.grecaptcha.render(containerRef.current, {
          sitekey: recaptchaSiteKey,
          callback: (token: string) => {
            cb.current(token);
          },
          'expired-callback': () => cb.current(''),
          'error-callback': () => cb.current(''),
        });
      } catch (err) {
        /* Ignore if already rendered */
      }
    });

    return () => {
      cancelled = true;
    };
  }, [recaptchaSiteKey]);

  if (!recaptchaSiteKey) {
    return null;
  }

  return (
    <div className="my-4 flex justify-center">
      <div ref={containerRef}></div>
    </div>
  );
});

export default Captcha;

